import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const storySteps = [
  {
    id: 1,
    title: "Fase 1: Il Bivio del Mattino",
    text: "La sveglia suona alle 7:30. Davanti a te si apre un portale dopaminergico. Sul comodino c'è lo smartphone con le notifiche di Reddit e Instagram illuminate, mentre dall'altra parte c'è un semplice bicchiere d'acqua e la luce del sole che filtra. Il boss 'Procrastinazione' ti sussurra: 'Solo 5 minuti di scrolling prima di alzarti...'",
    options: [
      {
        text: "Cediti al portale: sblocchi il telefono 'solo per svegliarti' e ti immergi nei feed.",
        effect: "evitamento",
        statsText: "+10 Scrolling, -5 Energia reale",
      },
      {
        text: "Esegui un Roll di Volontà: bevi il bicchiere d'acqua, ignori lo schermo e guardi fuori dalla finestra per 2 minuti.",
        effect: "azione",
        statsText: "+15 Autodisciplina, +10 Energia reale",
      }
    ]
  },
  {
    id: 2,
    title: "Fase 2: La Quest del Progetto",
    text: "Sono le 10:00. Hai un progetto lavorativo critico o una sessione di studio spaventosa da iniziare. Il task ti sembra una montagna d'acciaio senza spigli. La tua mente, in preda all'ansia, ti suggerisce che dovresti 'pulire a fondo la scrivania' o ordinare i tuoi anime preferiti prima di metterti all'opera.",
    options: [
      {
        text: "Avvii la side-quest di pulizia o apri YouTube in background 'per fare rumore bianco'.",
        effect: "evitamento",
        statsText: "+20 Procrastinazione Attiva, -10 Focus",
      },
      {
        text: "Applichi la tecnica 'Pomodoro a Barra Corta': metti lo smartphone in un'altra stanza, imposti un timer di 15 minuti e scrivi la prima riga.",
        effect: "azione",
        statsText: "+25 Focus Reale, +15 Azione",
      }
    ]
  },
  {
    id: 3,
    title: "Fase 3: Il Boss del Pomeriggio",
    text: "Sono le 15:30. Il pranzo è passato e ti senti esausto. La tua barra dell'energia è al 10%. La tentazione di stenderti sul divano per un pisolino rapido o fare una partita veloce online per ricaricarti è quasi irresistibile. Sai che una scelta sbagliata farà evaporare il resto del pomeriggio.",
    options: [
      {
        text: "Ti sdrai sul divano per '15 minuti' (consapevole che ti risveglierai rintronato alle 18:00).",
        effect: "evitamento",
        statsText: "+15 Sonno Profondo, -20 Controllo del Tempo",
      },
      {
        text: "Esegui una Quest Fisica di 5 minuti: esci a fare una camminata veloce senza cuffie o fai 10 flessioni sul posto per riattivare la biologia.",
        effect: "azione",
        statsText: "+20 Resilienza Fisica, +15 Chiarezza Mentale",
      }
    ]
  }
]

const profiles = {
  player_bloccato: {
    name: "Il Player Bloccato",
    desc: "Hai un potenziale enorme e una mente intelligente, ma vivi in perenne safe zone. Consumi più contenuti di quanti ne crei. La tua barra dell'Azione è bloccata da loop dopaminergici ed evitamento.",
    color: "#8b5cf6",
    stats: { Focus: 35, Energia: 30, Azione: 15, Costanza: 25 },
  },
  rogue_invisibile: {
    name: "Il Rogue Invisibile",
    desc: "Capisci perfettamente come funzionano le cose e osservi nell'ombra, ma eviti il confronto diretto con la realtà per paura del fallimento. Hai buoni picchi di focus, ma la costanza è instabile.",
    color: "#9ca3af",
    stats: { Focus: 55, Energia: 35, Azione: 25, Costanza: 40 },
  },
  eroe_in_addestramento: {
    name: "L'Eroe in Addestramento",
    desc: "Hai già un buon livello di consapevolezza e provi a combattere, ma ti manca un sistema strutturato di tracking e progressione. Soffri di sbalzi di energia e hai bisogno di alleanze per non mollare.",
    color: "#facc15",
    stats: { Focus: 65, Energia: 50, Azione: 45, Costanza: 35 },
  }
}

export default function InteractiveLibroGame() {
  const [gameState, setGameState] = useState('intro') // intro, playing, results
  const [currentStep, setCurrentStep] = useState(0)
  const [choices, setChoices] = useState([])
  const [email, setEmail] = useState('')
  const [subStatus, setSubStatus] = useState('idle') // idle, loading, success
  const [progress, setProgress] = useState(0)
  const [loadingText, setLoadingText] = useState('Analisi dei dati della quest...')

  // L'avanzamento del caricamento e la chiamata API vengono gestiti direttamente in handleSubscribe

  useEffect(() => {
    if (progress > 0 && progress < 30) {
      setLoadingText('Calcolo dei bias dopaminergici...')
    } else if (progress >= 30 && progress < 65) {
      setLoadingText('Valutazione dell\'indice di Evitamento...')
    } else if (progress >= 65 && progress < 90) {
      setLoadingText('Generazione della build ottimizzata...')
    } else if (progress >= 90 && progress < 100) {
      setLoadingText('Salvataggio checkpoint reale...')
    } else if (progress === 100) {
      setLoadingText('Analisi Completata! ⚔️')
    }
  }, [progress])

  const handleStart = () => {
    setChoices([])
    setCurrentStep(0)
    setGameState('playing')
  }

  const handleSelectOption = (effect) => {
    const updatedChoices = [...choices, effect]
    setChoices(updatedChoices)

    if (currentStep < storySteps.length - 1) {
      setCurrentStep((c) => c + 1)
    } else {
      setGameState('results')
    }
  }

  // Calculate profile based on options chosen
  const getCalculatedProfile = () => {
    const avoidCount = choices.filter(c => c === 'evitamento').length
    if (avoidCount >= 2) {
      return profiles.player_bloccato
    } else if (avoidCount === 1) {
      return profiles.rogue_invisibile
    } else {
      return profiles.eroe_in_addestramento
    }
  }

  const handleSubscribe = async (e) => {
    e.preventDefault()
    if (!email) return
    setSubStatus('loading')
    setProgress(10)

    const avoidCount = choices.filter(c => c === 'evitamento').length
    let calculatedProfileId = 'player_bloccato'
    if (avoidCount === 1) {
      calculatedProfileId = 'rogue_invisibile'
    } else if (avoidCount === 0) {
      calculatedProfileId = 'eroe_in_addestramento'
    }

    try {
      const progressTimer = setInterval(() => {
        setProgress(p => {
          if (p >= 90) {
            clearInterval(progressTimer)
            return 90
          }
          return p + Math.floor(Math.random() * 8) + 4
        })
      }, 80)

      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          profile: calculatedProfileId,
          choices
        })
      })

      clearInterval(progressTimer)

      if (response.ok) {
        setProgress(100)
        setTimeout(() => {
          setSubStatus('success')
        }, 400)
      } else {
        const errorData = await response.json()
        alert(`Errore del checkpoint: ${errorData.error || 'Impossibile salvare la build'}`)
        setSubStatus('idle')
      }
    } catch (err) {
      console.error(err)
      alert('Errore di connessione: il checkpoint reale è offline.')
      setSubStatus('idle')
    }
  }

  const currentProfile = gameState === 'results' ? getCalculatedProfile() : null

  return (
    <div
      className="manga-panel rounded-2xl p-6 md:p-10 relative overflow-hidden"
      style={{
        border: '1px solid rgba(139,92,246,0.3)',
        boxShadow: '0 0 35px rgba(139,92,246,0.1), 0 0 70px rgba(0,212,255,0.05)',
        background: 'rgba(5, 5, 20, 0.8)',
        backdropFilter: 'blur(12px)',
      }}
    >
      {/* Decorative details */}
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-nerd-purple/40 to-transparent" />
      <div className="absolute top-2 left-2 w-3 h-3 border-l border-t border-nerd-purple/30" />
      <div className="absolute top-2 right-2 w-3 h-3 border-r border-t border-nerd-purple/30" />
      <div className="absolute bottom-2 left-2 w-3 h-3 border-l border-b border-nerd-purple/30" />
      <div className="absolute bottom-2 right-2 w-3 h-3 border-r border-b border-nerd-purple/30" />

      <AnimatePresence mode="wait">
        {/* STATE 1: INTRO */}
        {gameState === 'intro' && (
          <motion.div
            key="intro"
            className="text-center space-y-6 max-w-2xl mx-auto py-4"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
          >
            <div className="inline-block px-3 py-1 rounded bg-nerd-purple/10 border border-nerd-purple/30">
              <span className="text-[9px] uppercase tracking-widest text-nerd-cyan font-display font-bold">
                🎮 MINI QUEST INTERATTIVA
              </span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-nerd-white">
              Il Risveglio del Campione
            </h3>
            <p className="text-nerd-gray text-sm md:text-base leading-relaxed">
              Affronta una giornata tipo da videogiocatore bloccato. Le tue scelte in questo breve racconto a bivio sveleranno il tuo <strong>Profilo di Procrastinazione reale</strong> e calcoleranno le tue statistiche attuali di Focus ed Energia.
            </p>
            <div className="pt-4">
              <button
                onClick={handleStart}
                className="py-3 px-8 nerd-btn-primary font-display text-xs font-bold uppercase tracking-wider transition-all duration-300"
                style={{ boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)' }}
              >
                Inizia il Libro-Game →
              </button>
            </div>
          </motion.div>
        )}

        {/* STATE 2: PLAYING */}
        {gameState === 'playing' && (
          <motion.div
            key="playing"
            className="space-y-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <div className="flex justify-between items-center border-b border-nerd-purple/20 pb-3">
              <span className="font-display text-xs font-bold text-nerd-cyan uppercase tracking-widest">
                {storySteps[currentStep].title}
              </span>
              <span className="font-display text-xs text-nerd-gray/60 font-bold">
                PASSO {storySteps[currentStep].id} / 3
              </span>
            </div>

            {/* Narrative Text */}
            <p className="text-nerd-white text-sm md:text-base leading-relaxed font-body bg-nerd-dark/40 p-4 rounded-xl border border-nerd-purple/10">
              {storySteps[currentStep].text}
            </p>

            {/* Options */}
            <div className="space-y-3 pt-2">
              {storySteps[currentStep].options.map((opt, oIdx) => (
                <button
                  key={oIdx}
                  onClick={() => handleSelectOption(opt.effect)}
                  className="w-full text-left p-4 rounded-xl border border-nerd-purple/20 bg-nerd-dark/60 hover:bg-nerd-purple/10 hover:border-nerd-cyan/50 hover:shadow-[0_0_15px_rgba(0,212,255,0.08)] transition-all group duration-200"
                >
                  <div className="flex gap-3 items-start">
                    <span className="h-5 w-5 rounded-full border border-nerd-purple/40 text-[10px] flex items-center justify-center font-bold text-nerd-cyan group-hover:border-nerd-cyan group-hover:bg-nerd-cyan/10 shrink-0 mt-0.5 font-display">
                      {oIdx === 0 ? 'A' : 'B'}
                    </span>
                    <div className="space-y-1">
                      <p className="text-nerd-gray group-hover:text-nerd-white text-xs md:text-sm transition-colors font-body">
                        {opt.text}
                      </p>
                      <span className="text-[10px] text-nerd-cyan/50 font-display block uppercase group-hover:text-nerd-cyan">
                        {opt.statsText}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Progress bar */}
            <div className="h-1 w-full bg-nerd-bg rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-nerd-purple to-nerd-cyan transition-all duration-300"
                style={{ width: `${((currentStep) / storySteps.length) * 100}%` }}
              />
            </div>
          </motion.div>
        )}

        {/* STATE 3: RESULTS & LEAD CAPTURE */}
        {gameState === 'results' && (
          <motion.div
            key="results"
            className="space-y-6"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="text-center border-b border-nerd-purple/20 pb-4 mb-4">
              <span className="font-display text-xs font-bold text-nerd-cyan uppercase tracking-widest block mb-1">
                🏆 QUEST COMPLETATA!
              </span>
              <h3 className="font-display text-2xl font-bold text-nerd-white">
                Analisi del Profilo Completata
              </h3>
            </div>

            {subStatus !== 'success' ? (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                {/* Profile breakdown */}
                <div className="lg:col-span-7 space-y-4">
                  <div className="flex items-center gap-3 justify-center lg:justify-start">
                    <span className="h-2 w-2 rounded-full animate-ping" style={{ backgroundColor: currentProfile.color }} />
                    <h4 className="font-display text-xl font-bold" style={{ color: currentProfile.color }}>
                      Classe: {currentProfile.name}
                    </h4>
                  </div>
                  <p className="text-nerd-gray text-xs md:text-sm leading-relaxed font-body">
                    {currentProfile.desc}
                  </p>

                  {/* Profile stats */}
                  <div className="space-y-2 pt-2 bg-nerd-dark/40 p-4 rounded-xl border border-nerd-purple/10 max-w-md mx-auto lg:mx-0">
                    <span className="text-[10px] text-nerd-cyan uppercase font-display block tracking-wider mb-2">Statistiche della Classe:</span>
                    {Object.entries(currentProfile.stats).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2">
                        <span className="text-[9px] text-nerd-gray uppercase w-16 shrink-0 font-display">{k}</span>
                        <div className="flex-1 h-1 bg-nerd-bg rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${v}%`, backgroundColor: currentProfile.color }}
                          />
                        </div>
                        <span className="text-[10px] font-display w-6 text-right" style={{ color: currentProfile.color }}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Email Form */}
                <div className="lg:col-span-5 w-full">
                  <div className="p-5 rounded-xl border border-nerd-purple/20 bg-nerd-dark/90 text-center">
                    <AnimatePresence mode="wait">
                      {subStatus === 'idle' && (
                        <motion.form
                          key="form"
                          onSubmit={handleSubscribe}
                          className="space-y-4"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <span className="font-display text-xs text-nerd-white font-bold tracking-wider uppercase block">
                            SALVA LA TUA BUILD ⚔️
                          </span>
                          <p className="text-[10px] text-nerd-gray leading-relaxed font-body">
                            Inserisci la tua email per salvare il tuo profilo e sbloccare gratis **La Guida di Sopravvivenza** con le 5 micro-quest per iniziare a potenziare la tua classe oggi stesso!
                          </p>

                          <div className="space-y-1 text-left">
                            <input
                              type="email"
                              required
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              placeholder="La tua email migliore..."
                              className="w-full px-3 py-2.5 rounded-lg text-xs bg-nerd-bg border border-nerd-purple/20 text-nerd-white placeholder-nerd-gray/40 focus:outline-none focus:border-nerd-cyan transition-all font-body"
                            />
                          </div>

                          <button
                            type="submit"
                            className="w-full py-2.5 nerd-btn-primary font-display text-[10px] font-bold uppercase tracking-wider transition-all duration-300"
                            style={{
                              boxShadow: `0 4px 12px ${currentProfile.color}35`,
                            }}
                          >
                            Ottieni la Guida Reale
                          </button>

                          <button
                            type="button"
                            onClick={handleStart}
                            className="text-[9px] font-display uppercase tracking-widest text-nerd-gray hover:text-nerd-cyan block mx-auto pt-2"
                          >
                            ← Rigiocati i bivi
                          </button>
                        </motion.form>
                      )}

                      {subStatus === 'loading' && (
                        <motion.div
                          key="loading"
                          className="py-6 space-y-4 text-center"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <span className="font-display text-xs text-nerd-white font-bold uppercase tracking-widest block">
                            {loadingText}
                          </span>

                          <div className="h-3 w-full bg-nerd-bg rounded-full p-[2px] border border-nerd-purple/25 overflow-hidden relative">
                            <motion.div
                              className="h-full bg-gradient-to-r from-nerd-purple to-nerd-cyan rounded-full"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            ) : (
              // Success Panel
              <motion.div
                key="success"
                className="py-8 text-center space-y-4 max-w-md mx-auto"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="w-12 h-12 rounded-full bg-nerd-cyan/10 border border-nerd-cyan text-nerd-cyan flex items-center justify-center mx-auto animate-bounce">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-6 h-6">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <div className="space-y-1">
                  <span className="font-display text-lg text-nerd-gold font-bold uppercase tracking-widest block">
                    QUEST COMPLETATA CON SUCCESSO!
                  </span>
                  <span className="font-display text-[9px] text-nerd-cyan uppercase tracking-widest block">
                    RISCATTO EQUIPAGGIAMENTO ATTIVO
                  </span>
                </div>
                <p className="text-nerd-gray text-xs leading-relaxed font-body">
                  La tua classe **{currentProfile.name}** è stata registrata! Abbiamo inviato la tua **Guida di Sopravvivenza personalizzata in PDF** all'indirizzo <strong>{email}</strong>.
                </p>
                <div style={{ margin: '20px 0' }}>
                  <a
                    href="/guida-di-sopravvivenza.pdf"
                    download="Guida_Sopravvivenza_Avatar_Reale.pdf"
                    className="nerd-btn-primary py-2.5 px-6 font-display text-[10px] font-bold uppercase tracking-wider transition-all duration-300 inline-block"
                    style={{
                      boxShadow: '0 4px 15px rgba(0, 212, 255, 0.35)',
                    }}
                  >
                    Scarica la Guida in PDF 📥
                  </a>
                </div>
                <p className="text-[10px] text-nerd-gray/60 leading-relaxed font-body italic">
                  Apri il tuo client di posta reale e cerca il mittente Amedeo @ Nerd Coach. Controlla anche nello spam se tardasse ad arrivare!
                </p>
                <div className="pt-2">
                  <button
                    onClick={() => {
                      setGameState('intro')
                      setSubStatus('idle')
                      setEmail('')
                    }}
                    className="text-[9px] font-display uppercase tracking-widest text-nerd-cyan hover:underline"
                  >
                    Ricomincia il Libro-Game
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
